import React from 'react'
import ReactDOM from 'react-dom/client'
import { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { RiCheckboxCircleLine } from 'react-icons/ri'
import {
  Snippet,
  Source,
  CampaignDetails,
  CampaignDestination,
  FinalPage,
  PostbackJS,
} from '../components/NewTracker/Index'
import '../layouts/stylesheets/new-tracker-page-style.css'
import SideMenu from '../layouts/SideMenu'
import CampaignType from '../components/NewTracker/CampaignType'
import CampaignObjective from '../components/NewTracker/CampaignObjective'
import MenuWrap from '../components/BurgerMenu/MenuWrap'

import { ReactComponent as BackButton } from '../assets/back-button.svg'
import { ReactComponent as ForwardButton } from '../assets/forward-button.svg'

const NewTrackerPage = () => {
  const location = useLocation()
  const [activePage, setActivePage] = useState(
    location.state?.activePage ?? 'source'
  )
  const [pageType] = useState(location.state?.pageType ?? 'newTracker')
  const [campaignData, setCampaignData] = useState({})
  const [nav, setNav] = useState('')
  const [isNavDisabled, setIsNavDisabled] = useState(false)
  const isCodeSnippetPage = activePage === 'snippet' || activePage === 'postbackJS'

  const handleNav = (event) => {
    if (isNavDisabled) {
      return
    }

    const id = event?.currentTarget?.id
    if (id) setNav(id)
  }

  const root = useRef()
  const progressBar = useRef()

  useEffect(() => {
    if (activePage !== 'finalPage') {
      let order = {}
      if (!root.current) {
        root.current = ReactDOM.createRoot(progressBar.current)
      }
      if (pageType === 'firstTracker') {
        order.snippet = 0
        order.postbackJS = 1
        order.source = 2
        order.campaignType = 3
        order.campaignObjective = 4
        order.campaignDetails = 5
        order.campaignDestination = 6
      } else {
        order.source = 0
        order.campaignType = 1
        order.campaignObjective = 2
        order.campaignDetails = 3
        order.campaignDestination = 4
      }
      const gridProps = { className: 'grid' }
      // Tailwind not detecting class dynamically so had to add class like this.
      if (Object.keys(order).length === 5) {
        gridProps.className = gridProps.className + ' grid-cols-5'
      } else {
        gridProps.className = gridProps.className + ' grid-cols-7'
      }
      const activePageOrder = order[activePage]
      let container = []
      for (let i = 0; i < Object.keys(order).length; i++) {
        const props = {
          className: 'flex justify-center items-center col-span-1',
          key: i,
        }
        if (i <= activePageOrder) {
          container.push(
            React.createElement(
              'div',
              props,
              React.createElement(
                'div',
                { className: 'nav-dot flex items-center justify-center' },
                React.createElement(RiCheckboxCircleLine, {
                  className: 'text-white w-2/3 h-2/3',
                })
              )
            )
          )
        } else {
          container.push(
            React.createElement(
              'div',
              props,
              React.createElement('div', { className: 'nav-dot' })
            )
          )
        }
      }
      const containerGrid = React.createElement('div', gridProps, container)
      root.current.render(containerGrid)
    } else {
      root.current.render('')
    }
  }, [activePage, pageType])

  return (
    <div id="outer-container" style={{ height: '100%' }}>
      <MenuWrap className="sm:hidden">
        <SideMenu isAuthorized={true}></SideMenu>
      </MenuWrap>
      <div id="page-wrap">
        <div className="flex">
          <SideMenu isAuthorized={true} className="hidden sm:block"></SideMenu>
          <div className="w-full min-w-0">
            <div className="relative flex min-h-screen flex-col">
              {activePage !== 'finalPage' && (
                <button
                  type="button"
                  id="back"
                  onClick={handleNav}
                  disabled={isNavDisabled}
                  className="absolute left-2 top-1/2 z-20 -translate-y-1/2 rounded-full bg-white/90 p-2 shadow-md transition disabled:cursor-not-allowed disabled:opacity-40 sm:left-4"
                  aria-label="Back"
                >
                  <BackButton className="h-7 w-7 cursor-pointer sm:h-8 sm:w-8" />
                </button>
              )}
              <div
                className={`flex min-h-[calc(100vh-120px)] w-full items-center justify-center py-8 ${
                  isCodeSnippetPage
                    ? 'px-4 sm:px-6 md:px-8'
                    : 'px-8 sm:px-12 lg:px-20 xl:px-24'
                }`}
                aria-live="polite"
              >
                {activePage === 'snippet' && pageType === 'firstTracker' && (
                  <Snippet
                    pageType={pageType}
                    nav={nav}
                    setNav={setNav}
                    setActivePage={setActivePage}
                    campaignData={campaignData}
                    setCampaignData={setCampaignData}
                    setIsNavDisabled={setIsNavDisabled}
                  />
                )}
                {activePage === 'postbackJS' && pageType === 'firstTracker' && (
                  <PostbackJS
                    pageType={pageType}
                    nav={nav}
                    setNav={setNav}
                    setActivePage={setActivePage}
                    campaignData={campaignData}
                    setCampaignData={setCampaignData}
                    setIsNavDisabled={setIsNavDisabled}
                  />
                )}
                {activePage === 'source' && (
                  <Source
                    pageType={pageType}
                    nav={nav}
                    setNav={setNav}
                    setActivePage={setActivePage}
                    campaignData={campaignData}
                    setCampaignData={setCampaignData}
                    setIsNavDisabled={setIsNavDisabled}
                  />
                )}
                {activePage === 'campaignDetails' && (
                  <CampaignDetails
                    pageType={pageType}
                    nav={nav}
                    setNav={setNav}
                    setActivePage={setActivePage}
                    campaignData={campaignData}
                    setCampaignData={setCampaignData}
                    setIsNavDisabled={setIsNavDisabled}
                  />
                )}
                {activePage === 'campaignDestination' && (
                  <CampaignDestination
                    pageType={pageType}
                    nav={nav}
                    setNav={setNav}
                    setActivePage={setActivePage}
                    campaignData={campaignData}
                    setCampaignData={setCampaignData}
                    setIsNavDisabled={setIsNavDisabled}
                  />
                )}
                {activePage === 'campaignType' && (
                  <CampaignType
                    nav={nav}
                    setNav={setNav}
                    setActivePage={setActivePage}
                    campaignData={campaignData}
                    setCampaignData={setCampaignData}
                    setIsNavDisabled={setIsNavDisabled}
                  />
                )}
                {activePage === 'campaignObjective' && (
                  <CampaignObjective
                    nav={nav}
                    setNav={setNav}
                    setActivePage={setActivePage}
                    campaignData={campaignData}
                    setCampaignData={setCampaignData}
                    setIsNavDisabled={setIsNavDisabled}
                  />
                )}
                {activePage === 'finalPage' && (
                  <FinalPage
                    nav={nav}
                    setNav={setNav}
                    setActivePage={setActivePage}
                    campaignData={campaignData}
                    setCampaignData={setCampaignData}
                    setIsNavDisabled={setIsNavDisabled}
                  />
                )}
              </div>
              {activePage !== 'finalPage' && (
                <button
                  type="button"
                  id="forward"
                  onClick={handleNav}
                  disabled={isNavDisabled}
                  className="absolute right-2 top-1/2 z-20 -translate-y-1/2 rounded-full bg-white/90 p-2 shadow-md transition disabled:cursor-not-allowed disabled:opacity-40 sm:right-4"
                  aria-label="Forward"
                >
                  <ForwardButton className="h-7 w-7 cursor-pointer sm:h-8 sm:w-8" />
                </button>
              )}
              <div
                id="progressBar"
                ref={progressBar}
                className="mx-auto w-full max-w-xl px-6 pb-8"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NewTrackerPage
